<script setup lang="ts">
/**
 * Label/value pairs as a description list — the facts of a record, a mount,
 * a run. `layout` picks the arrangement: `stacked` (label over value, one per
 * row), `inline` (labels in a left column, values beside them) or `grid`
 * (label-over-value cells that fill as many columns as fit). An item with
 * `full` spans the whole row in `grid`.
 *
 * `align` is `start` (the default) or `end`. `end` right-aligns the values
 * and sets tabular figures, on `inline` and `stacked`. `grid` stays
 * start-aligned: its cells wrap, so a shared right edge would not line the
 * figures up.
 *
 * Values are text by default; `mono` sets ids, paths and hashes in the
 * monospace face, `hint` adds a muted line under the value, `strong` sets
 * the label and the value in the semibold weight (the hint stays regular).
 * An empty value (`null`, `undefined`, `''`) shows a muted dash rather than
 * a blank. The `value` slot renders rich values (a badge, a link, a copy
 * field) per item.
 *
 * A written calculation (`inline`): `divided` (default `true`) draws a
 * hairline between the items; without it the rows run free, an item with
 * `separated` starts a new group under a full-width rule, and an item with
 * `result` is the outcome of the rows above it — a short sum rule over its
 * value, as on paper (BLP/nx#3198).
 */
const props = withDefaults(
  defineProps<{
    items: readonly NxKeyValueItem[];
    layout?: 'stacked' | 'inline' | 'grid';
    /**
     * `end` right-aligns the values and sets tabular figures on `inline` and
     * `stacked`. `grid` stays start-aligned.
     */
    align?: 'start' | 'end';
    /** Tighter spacing for side panels and drawers. */
    dense?: boolean;
    /** Text for an empty value. */
    emptyText?: string;
    /**
     * `inline` only: a hairline between the items (default). `false` lets the
     * rows run free; `separated` and `result` items then draw their rules.
     */
    divided?: boolean;
  }>(),
  { layout: 'grid', align: 'start', dense: false, emptyText: '—', divided: true }
);

defineSlots<{
  value?: (props: { item: NxKeyValueItem }) => unknown;
}>();

function isEmpty(value: NxKeyValueItem['value']): boolean {
  return value === null || value === undefined || value === '';
}
</script>

<script lang="ts">
export interface NxKeyValueItem {
  key: string | number;
  label: string;
  value?: string | number | null;
  /** Monospace value: ids, paths, hashes, URLs. */
  mono?: boolean;
  /** A muted line under the value. */
  hint?: string;
  /** Spans the whole row in the `grid` layout (long text, a JSON block). */
  full?: boolean;
  /** Label and value in the semibold weight. The hint stays regular. */
  strong?: boolean;
  /** `inline`: a full-width rule above the item — it starts a new group. */
  separated?: boolean;
  /**
   * `inline`: the outcome of the rows above — a sum rule over the value, as
   * wide as the figure, as in a written calculation.
   */
  result?: boolean;
}
</script>

<template>
  <dl
    class="nx-kv"
    :class="[
      `layout-${props.layout}`,
      {
        'is-dense': props.dense,
        'is-undivided': !props.divided && props.layout === 'inline',
        'align-end': props.align === 'end' && props.layout !== 'grid',
      },
    ]"
    data-testid="nx-key-value-list"
  >
    <div
      v-for="item in props.items"
      :key="item.key"
      class="nx-kv__item"
      :class="{
        'is-full': item.full,
        'is-strong': item.strong,
        'is-separated': item.separated,
        'is-result': item.result,
      }"
    >
      <dt class="nx-kv__label">{{ item.label }}</dt>
      <dd class="nx-kv__value">
        <slot name="value" :item="item">
          <span v-if="isEmpty(item.value)" class="nx-kv__empty">{{ props.emptyText }}</span>
          <span v-else class="nx-kv__text" :class="{ 'is-mono': item.mono }">{{ item.value }}</span>
        </slot>
        <span v-if="item.hint" class="nx-kv__hint">{{ item.hint }}</span>
      </dd>
    </div>
  </dl>
</template>

<style scoped>
.nx-kv {
  --kv-gap-y: var(--nxt-space-md);
  --kv-gap-x: var(--nxt-space-lg);

  display: grid;
  gap: var(--kv-gap-y) var(--kv-gap-x);
  margin: 0;
  min-width: 0;
}
.nx-kv.is-dense {
  --kv-gap-y: var(--nxt-space-sm);
  --kv-gap-x: var(--nxt-space-md);
}
.nx-kv.layout-grid {
  grid-template-columns: repeat(auto-fill, minmax(min(100%, var(--nxt-panel-width-xs)), 1fr));
}
.nx-kv__item {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-kv__item.is-full {
  grid-column: 1 / -1;
}

/* Inline: one shared label column, values aligned beside it. */
.nx-kv.layout-inline {
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 0;
}
.nx-kv.layout-inline .nx-kv__item {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  align-items: baseline;
  gap: var(--kv-gap-x);
  padding-block: calc(var(--kv-gap-y) / 2);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-kv.layout-inline .nx-kv__item:last-child {
  border-block-end: 0;
}
/* A written calculation: the rows run free, the rules say what belongs
   together — a group's full-width rule, the sum rule over a result. */
.nx-kv.layout-inline.is-undivided .nx-kv__item {
  border-block-end: 0;
}
.nx-kv.layout-inline .nx-kv__item.is-separated {
  border-block-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-kv.layout-inline .nx-kv__item.is-result .nx-kv__value > :first-child {
  align-self: flex-start;
  padding-block-start: var(--nxt-space-2xs);
  border-block-start: var(--nxt-hairline) solid var(--nxt-border-strong);
  /* At least as wide as an amount, so a short result keeps a full rule. */
  min-inline-size: 6em;
}
.nx-kv.layout-inline.align-end .nx-kv__item.is-result .nx-kv__value > :first-child {
  align-self: flex-end;
}

.nx-kv__label {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.nx-kv.layout-inline .nx-kv__label {
  max-width: var(--nxt-panel-width-sm);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  letter-spacing: 0;
  text-transform: none;
}
/* After the inline label weight, so a strong inline label wins the tie. */
.nx-kv__item.is-strong .nx-kv__label,
.nx-kv__item.is-strong .nx-kv__value {
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-kv__item.is-strong .nx-kv__hint {
  font-weight: var(--nxt-font-weight-regular);
}
.nx-kv__value {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  margin: 0;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  overflow-wrap: anywhere;
}
/* The class is only set for inline and stacked; grid stays start-aligned. */
.nx-kv.align-end .nx-kv__value {
  text-align: end;
  font-variant-numeric: tabular-nums;
}
.nx-kv:not(.is-dense) .nx-kv__value {
  font-size: var(--nxt-font-size-md);
}
.nx-kv__text.is-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.9em;
}
.nx-kv__empty {
  color: var(--nxt-fg-muted);
}
.nx-kv__hint {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
</style>
